import React from 'react';

/**
 * Countertop schematic — edge-on slab.
 *
 * The countertop is essentially a horizontal slab; in any elevation
 * we always see it from the side as a thin horizontal band. There is
 * no meaningful "variant" field worth dispatching on — a kitchen slab
 * and a vanity slab look the same edge-on. Optional `obj.texture` /
 * `obj.color` provide a fill hint; if absent we leave it unfilled.
 *
 * The plan calls for ~1.25" total slab thickness. We do not honor
 * heightMeters directly: the parent <div> already sizes the
 * schematic to the object's bounding box. We just draw two parallel
 * horizontals at the top and bottom of that box and (optionally) a
 * faint fill so the slab reads as solid stone.
 */

/**
 * An explicit slab colour, or null for the unfilled outline.
 *
 * This is the ONLY place in the elevation schematics that can paint an
 * arbitrary colour — across all 78 of them every other fill is `strokeColor`,
 * `white`, or `none`. Elevations are a monochrome line drawing, on screen and
 * in the construction-document PDFs, so keep it that way: do NOT wire this to
 * a texture tint. A textured countertop would then render brown in a B&W
 * drawing, and 240 countertops in dev carry a texture.
 *
 * The `obj.texture.color` branch that used to live here could never fire: an
 * object's `texture` is `{ textureId, regions? }` and 0 of 914 textured
 * objects carry a `color`. `obj.color` is likewise unset on all 637
 * countertops in dev, so this returns null today in every real case — the
 * hook is kept only for an explicitly-coloured slab.
 */
function pickFill(obj) {
  if (!obj) return null;
  if (typeof obj.color === 'string') return obj.color;
  return null;
}

export function CountertopSchematic({
  widthMeters: width,
  heightMeters: height,
  depthMeters: depth,
  view = 'front',
  obj = {},
  strokeColor,
  strokeWidth,
}) {
  const fill = pickFill(obj);
  const isSide = view === 'left' || view === 'right';
  const viewportW = isSide ? depth : width;
  return (
    <svg
      viewBox={`0 0 ${viewportW} ${height}`}
      preserveAspectRatio="xMidYMid meet"
      style={{ width: '100%', height: '100%', overflow: 'visible' }}
    >
      <rect
        x={0}
        y={0}
        width={viewportW}
        height={height}
        fill={fill || 'none'}
        fillOpacity={fill ? 0.35 : 0}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
      {/* Inner hairline reinforces the slab read in tall elevations.
          Side views are typically only 1.5" tall so the hairline would
          read as visual noise — skip it. */}
      {!isSide && (
        <line
          x1={0}
          y1={height / 2}
          x2={viewportW}
          y2={height / 2}
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          strokeOpacity={0.35}
          vectorEffect="non-scaling-stroke"
        />
      )}
    </svg>
  );
}

CountertopSchematic.supportsSideViews = true;
// A slab edge-on reads the same from either side.
CountertopSchematic.supportsBackView = true;
